import { c as _c } from "react/compiler-runtime";
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { IcoClose16, IcoDragAndDrop16, IcoFilter16, IcoImages24 } from '@onefootprint/icons';
import { Button, IconButton, Shimmer, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import Image from 'next/image';
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import panImage from './pan-image';
import type { ComparisonImage, ImageInspection, SlotIndex } from './types';
type ImagePaneProps = {
  image: ComparisonImage;
  slot: SlotIndex;
  onClear: () => void;
  inspection?: ImageInspection;
  onInspect: (point: ImageInspection) => void;
  onPan: (point: ImageInspection) => void;
  onDismiss: () => void;
  children?: (search: string) => ReactNode;
};
const bound = (value: number) => Math.max(0, Math.min(1, value));
const ImagePaneTitle = t0 => {
  const $ = _c(20);
  const {
    image,
    search,
    onSearch,
    canFilter
  } = t0;
  const searchRef = useRef(null);
  const filterRef = useRef(null);
  const [open, setOpen] = useState(false);
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (open) {
        searchRef.current?.focus();
      }
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== onSearch) {
    t3 = () => {
      setOpen(false);
      onSearch("");
      filterRef.current?.focus();
    };
    $[3] = onSearch;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const close = t3;
  const label = open ? `Close field search in ${image.label}` : `Find field in ${image.label}`;
  let t4;
  if ($[5] !== close || $[6] !== image.documentIdentifier || $[7] !== image.label || $[8] !== onSearch || $[9] !== open || $[10] !== search) {
    t4 = open ? <input ref={searchRef} type="search" aria-label={`Find field in ${image.label}`} placeholder="Find field" value={search} onChange={event => onSearch(event.target.value)} onKeyDown={event_0 => {
      if (event_0.key !== "Escape") {
        return;
      }
      event_0.preventDefault();
      event_0.stopPropagation();
      close();
    }} className="h-7 min-w-0 flex-1 rounded-sm bg-secondary px-2 text-body-2 text-primary outline-none placeholder:text-tertiary focus-visible:outline-2 focus-visible:outline-accent" /> : <h3 className="min-w-0 flex-1 truncate text-label-2 text-primary" title={image.documentIdentifier}>{image.label}</h3>;
    $[5] = close;
    $[6] = image.documentIdentifier;
    $[7] = image.label;
    $[8] = onSearch;
    $[9] = open;
    $[10] = search;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== canFilter || $[13] !== close || $[14] !== label || $[15] !== open) {
    t5 = canFilter && <Tooltip content={label}><IconButton ref={filterRef} icon={IcoFilter16} size="compact" aria-label={label} aria-expanded={open} onClick={() => open ? close() : setOpen(true)} /></Tooltip>;
    $[12] = canFilter;
    $[13] = close;
    $[14] = label;
    $[15] = open;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
const ImagePane = t0 => {
  const $ = _c(68);
  const {
    image,
    slot,
    onClear,
    inspection,
    onInspect,
    onPan,
    onDismiss,
    children
  } = t0;
  const t1 = `image:${slot}`;
  let t2;
  if ($[0] !== image.id) {
    t2 = {
      imageId: image.id
    };
    $[0] = image.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      id: t1,
      data: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    isDragging
  } = useDraggable(t3);
  const imageRef = useRef(null);
  const [search, setSearch] = useState("");
  let t4;
  if ($[5] !== image.height || $[6] !== image.width) {
    t4 = {
      width: image.width,
      height: image.height
    };
    $[5] = image.height;
    $[6] = image.width;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [dimensions, setDimensions] = useState(t4);
  const nativeWidth = useRef(image.width);
  const gesture = useRef(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const ready = image.status === "ready" && !failed;
  const scale = inspection?.scale ?? 1;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => ({
      x: 0.5,
      y: 0.5,
      scale: Math.max(2.5, nativeWidth.current / (imageRef.current?.getBoundingClientRect().width ?? nativeWidth.current))
    });
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const center = t5;
  let t6;
  if ($[9] !== inspection || $[10] !== onInspect) {
    t6 = event => {
      if (gesture.current?.moved) {
        gesture.current = null;
        return;
      }
      gesture.current = null;
      const bounds = imageRef.current?.getBoundingClientRect();
      const point = center();
      if (!inspection && bounds && event.detail !== 0) {
        point.x = bound((event.clientX - bounds.left) / bounds.width);
        point.y = bound((event.clientY - bounds.top) / bounds.height);
      }
      onInspect(point);
    };
    $[9] = inspection;
    $[10] = onInspect;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const inspectAt = t6;
  const t7 = image.label;
  let t8;
  if ($[12] !== inspection || $[13] !== onDismiss) {
    t8 = event_0 => {
      if (event_0.key !== "Escape" || !inspection) {
        return;
      }
      event_0.preventDefault();
      event_0.stopPropagation();
      onDismiss();
    };
    $[12] = inspection;
    $[13] = onDismiss;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== transform) {
    t9 = CSS.Translate.toString(transform);
    $[15] = transform;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = {
      transform: t9
    };
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = inspection ? "border-accent" : "border-tertiary";
  const t12 = isDragging && "z-20 opacity-60";
  let t13;
  if ($[19] !== t11 || $[20] !== t12) {
    t13 = cx("flex min-w-0 flex-col overflow-hidden rounded-lg border bg-primary", t11, t12);
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = `Drag ${image.label}`;
  let t15;
  if ($[22] !== attributes || $[23] !== listeners || $[24] !== setActivatorNodeRef || $[25] !== t14) {
    t15 = <IconButton ref={setActivatorNodeRef} icon={IcoDragAndDrop16} size="compact" className="touch-none cursor-grab" {...attributes} {...listeners} aria-label={t14} />;
    $[22] = attributes;
    $[23] = listeners;
    $[24] = setActivatorNodeRef;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = Boolean(children) && image.fields.length > 10;
  let t17;
  if ($[27] !== image || $[28] !== search || $[29] !== t16) {
    t17 = <ImagePaneTitle image={image} search={search} onSearch={setSearch} canFilter={t16} />;
    $[27] = image;
    $[28] = search;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = `Clear ${slot === 0 ? "left" : "right"} slot`;
  let t19;
  if ($[31] !== onClear || $[32] !== t18) {
    t19 = <IconButton icon={IcoClose16} size="compact" aria-label={t18} onClick={onClear} />;
    $[31] = onClear;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t15 || $[35] !== t17 || $[36] !== t19) {
    t20 = <div className="flex items-center gap-2 border-b border-tertiary px-2 py-2">{t15}{t17}{t19}</div>;
    $[34] = t15;
    $[35] = t17;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== dimensions || $[39] !== failed || $[40] !== image.height || $[41] !== image.id || $[42] !== image.isDecrypting || $[43] !== image.label || $[44] !== image.onDecrypt || $[45] !== image.onRetry || $[46] !== image.src || $[47] !== image.status || $[48] !== image.width || $[49] !== inspectAt || $[50] !== inspection || $[51] !== onPan || $[52] !== ready || $[53] !== retry || $[54] !== scale) {
    t21 = ready ? <button type="button" aria-label={`Inspect ${image.label}`} aria-pressed={!!inspection} className={cx("relative grid w-full aspect-[1.6] min-h-48 touch-none place-items-center overflow-hidden bg-canvas p-4 outline-offset-[-3px] focus-visible:outline focus-visible:outline-accent [container-type:size]", inspection ? "cursor-zoom-out active:cursor-grabbing" : "cursor-zoom-in")} onClick={inspectAt} onPointerDown={event_1 => {
      if (!inspection || event_1.button !== 0) {
        return;
      }
      event_1.preventDefault();
      event_1.stopPropagation();
      const bounds_0 = imageRef.current?.getBoundingClientRect();
      if (!bounds_0) {
        return;
      }
      event_1.currentTarget.setPointerCapture(event_1.pointerId);
      gesture.current = {
        x: event_1.clientX,
        y: event_1.clientY,
        width: bounds_0.width,
        height: bounds_0.height,
        inspection,
        moved: false
      };
    }} onPointerMove={event_2 => {
      const start = gesture.current;
      if (!start || event_2.buttons !== 1) {
        return;
      }
      const dx = event_2.clientX - start.x;
      const dy = event_2.clientY - start.y;
      if (Math.hypot(dx, dy) < 5 && !start.moved) {
        return;
      }
      start.moved = true;
      onPan(panImage(start.inspection, {
        width: start.width,
        height: start.height
      }, {
        x: dx,
        y: dy
      }));
    }} onPointerCancel={() => {
      gesture.current = null;
    }} onKeyDown={event_3 => {
      if (!inspection) {
        return;
      }
      const delta = {
        ArrowLeft: {
          x: -0.04,
          y: 0
        },
        ArrowRight: {
          x: 0.04,
          y: 0
        },
        ArrowUp: {
          x: 0,
          y: -0.04
        },
        ArrowDown: {
          x: 0,
          y: 0.04
        }
      };
      const step = delta[event_3.key];
      if (!step) {
        return;
      }
      event_3.preventDefault();
      onPan({
        ...inspection,
        x: bound(inspection.x + step.x),
        y: bound(inspection.y + step.y)
      });
    }}><div ref={imageRef} className="relative shrink-0 shadow-md" style={{
        width: `min(92cqw, ${92 * dimensions.width / dimensions.height}cqh)`,
        aspectRatio: `${dimensions.width} / ${dimensions.height}`,
        transform: `translate(${(0.5 - (inspection?.x ?? 0.5)) * scale * 100}%, ${(0.5 - (inspection?.y ?? 0.5)) * scale * 100}%) scale(${scale})`
      }}>{image.status === "ready" && <Image key={`${image.id}-${retry}`} src={image.src} alt={image.label} width={image.width} height={image.height} unoptimized={true} draggable={false} className="block h-full w-full select-none rounded-lg" onLoad={event_4 => {
          nativeWidth.current = event_4.currentTarget.naturalWidth;
          setDimensions({
            width: event_4.currentTarget.naturalWidth,
            height: event_4.currentTarget.naturalHeight
          });
        }} onError={() => setFailed(true)} />}</div></button> : image.status === "loading" || image.status === "locked" && image.isDecrypting ? <div role="status" aria-label={`Loading ${image.label}`} className="flex aspect-[1.6] min-h-48 items-center justify-center bg-canvas p-6"><Shimmer width="90%" height="160px" /></div> : <div role="status" className="flex aspect-[1.6] min-h-48 flex-col items-center justify-center gap-3 bg-canvas p-6 text-center"><IcoImages24 color="tertiary" /><span className="text-label-2 text-primary">{image.status === "locked" ? "Encrypted image" : "Image unavailable"}</span><p className="max-w-64 text-body-2 text-tertiary">{failed ? "The preview could not load." : {
          unavailable: "This exact document version is unavailable.",
          "no-entity": "Open this block in an investigation to view document images.",
          denied: "You do not have permission to decrypt this document.",
          redacted: "This image was redacted.",
          unsupported: "This reference does not contain a supported image.",
          error: "The document could not be loaded.",
          locked: "Decrypt to inspect this document.",
          ready: "",
          loading: ""
        }[image.status]}</p>{image.status === "locked" && <Button size="compact" onClick={image.onDecrypt}>Decrypt image</Button>}{image.status === "error" && <Button size="compact" variant="secondary" onClick={image.onRetry}>Retry document</Button>}{failed && <Button size="compact" variant="secondary" onClick={() => {
        setFailed(false);
        setRetry(_temp);
      }}>Retry image</Button>}</div>;
    $[38] = dimensions;
    $[39] = failed;
    $[40] = image.height;
    $[41] = image.id;
    $[42] = image.isDecrypting;
    $[43] = image.label;
    $[44] = image.onDecrypt;
    $[45] = image.onRetry;
    $[46] = image.src;
    $[47] = image.status;
    $[48] = image.width;
    $[49] = inspectAt;
    $[50] = inspection;
    $[51] = onPan;
    $[52] = ready;
    $[53] = retry;
    $[54] = scale;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== children || $[57] !== search) {
    t22 = children?.(search);
    $[56] = children;
    $[57] = search;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== image.label || $[60] !== setNodeRef || $[61] !== t10 || $[62] !== t13 || $[63] !== t20 || $[64] !== t21 || $[65] !== t22 || $[66] !== t8) {
    t23 = <section ref={setNodeRef} aria-label={t7} onKeyDown={t8} style={t10} className={t13}>{t20}{t21}{t22}</section>;
    $[59] = image.label;
    $[60] = setNodeRef;
    $[61] = t10;
    $[62] = t13;
    $[63] = t20;
    $[64] = t21;
    $[65] = t22;
    $[66] = t8;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  return t23;
};
export type { ImagePaneProps };
export default ImagePane;
function _temp(value) {
  return value + 1;
}